import {
  CalendarOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  UndoOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import * as Sentry from "@sentry/react";
import { Badge, Button, Dropdown, Flex, Popconfirm, Tooltip, Typography, message } from "antd";
import type { MenuProps } from "antd";
import type { AxiosError } from "axios";
import dayjs from "dayjs";
import React from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { GENDER_TYPE, getNotesHTMLStringFromItem, isLockedNoteOfANonPayingUser } from "../../domain/notes.domain";
import { shouldEnableGCSRecording } from "../../domain/user.domain";
import { useAppSelector } from "../../hooks/redux-hooks";
import { useIsMobileView } from "../../hooks/ui-hook";
import {
  handleDelete,
  handleSubmitNoteForProcessing,
  handleUploadAudioSubmission,
  handleUploadAudioSubmissionForTranscription,
  handleWriteNoteSubmission,
} from "../../service/notes.service";
import { resumableUploadManager } from "../../service/upload.service";
import {
  setCreateModalState,
  setShowDemoSignupModal,
  setShowRecordingView,
  setUploadType,
} from "../../slices/appStateSlice";
import { setRecordingDetail } from "../../slices/recordingsSlice";
import { setShowPaymentModal } from "../../slices/userSlice";
import type { AppDispatch } from "../../store";
import { ModalityMode, type TNotes } from "../../types/index.type";
import { EventType, trackEvent } from "../../utils/analytics";
import { assembleBlob } from "../../utils/audioStore";
import { PURGE_EXPIRED_AUDIO_FILES_DAYS } from "../../utils/constants";
import { showPaymentModal } from "../../utils/datamanipulation.utils";
import { eventEmitter } from "../../utils/eventEmitter";
import { isMaxTimedOut } from "../../utils/recording.utils";
import styles from "./notes.module.scss";

type TNotesListProps = {
  noteRef?: React.RefObject<HTMLDivElement>;
  item: TNotes;
  index: number;
  fetchNotes: (pageNumber?: number) => void;
  navigateToNotesDetail: (item: TNotes) => void;
};

declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: any) => void;
    };
  }
}

const NotesList = ({ noteRef, item, index, fetchNotes, navigateToNotesDetail }: TNotesListProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobileView();
  const { isDemoPath, currentPage, isMobilePath } = useAppSelector((state) => state.appState);
  const { userInfo } = useAppSelector((state) => state.auth);
  const userData = useAppSelector((state) => state.user.data);
  const { recordingDetail, isRecording, isPaused } = useAppSelector((state) => state.recordings);
  const [retryInProgress, setRetryInProgress] = React.useState(false);
  const componentName = "notes";
  const [isUploading, setIsUploading] = React.useState(false);
  const [isResuming, setIsResuming] = React.useState(false);
  const [resumeAudioId, setResumeAudioId] = React.useState<number | null>(null);

  const canRetry = React.useMemo(
    () => (item.modality_type === 0 || item.modality_type === 1) && !isMobilePath,
    [item.modality_type, isMobilePath],
  );

  const moreMenuItems: MenuProps["items"] = [
    {
      key: "retry",
      label: "Retry upload",
    },
  ];

  /**
   * Handling Delete of the Recording.
   * @param recordingId Recording ID
   * @returns
   */
  const handleDeleteRecording = async (recordingId: number) => {
    if (isDemoPath) {
      dispatch(setShowDemoSignupModal(true));
      return;
    }
    const loading = message.loading("Deleting Note..", 0);
    trackEvent(EventType.DELETE_NOTE, { recordingId });
    await handleDelete(recordingId)
      .then(() => {
        loading();
        message.success("Recording deleted successfully");
        fetchNotes(currentPage);
      })
      .catch((error: AxiosError) => {
        loading();
        message.error("Error deleting recording. Please try again.");
        console.error("Error deleting recording:", error);
      });
  };

  const handleOpenPaymentModal = () => {
    dispatch(setShowPaymentModal(true));
  };

  const uploadFileFromIndexedDBForTranscription = async (audioIDString: string) => {
    try {
      const blob = await assembleBlob(audioIDString);
      if (!blob || blob.size === 0) {
        const error = new Error("No audio file found in indexedDB for transcription");
        Sentry.captureException(error, { extra: { audio_id: audioIDString } });
        throw error;
      }
      const formData = new FormData();
      formData.append("audio_id", audioIDString);
      formData.append("audio", blob);

      const response = await handleUploadAudioSubmissionForTranscription(formData);
      if (response.status !== 200) {
        throw new Error("Error uploading audio from indexedDB for processing");
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          audio_id: audioIDString,
          error,
        },
      });
      throw error;
    }
  };

  const handleSubmitForProcessing = async (item: TNotes) => {
    trackEvent(EventType.PROCESS_NOW_BUTTON, {
      recordingId: item.id,
      modalityType: item.modality_type,
      noteType: item.note_type,
    });
    setIsUploading(true);
    try {
      //@ts-ignore
      if (
        isMobilePath &&
        window &&
        window.ReactNativeWebView &&
        window.ReactNativeWebView.postMessage &&
        item.system_metadata &&
        item.system_metadata.is_from_mobile
      ) {
        //@ts-ignore
        window.ReactNativeWebView.postMessage(
          JSON.stringify({
            type: "retrySubmission",
            recordingId: item.id,
            client_id: item.client,
          }),
        );
        return;
      }

      if (shouldEnableGCSRecording(userData?.id)) {
        // Check if there's a partial GCS upload that can be finalized
        // Check for EITHER local session OR GCS upload URL from system_metadata
        if (resumableUploadManager.hasPartialUploadAvailable(item.id) || item.system_metadata?.gcs_upload_url) {
          console.log(`Found partial GCS upload for audio ID ${item.id}. Attempting to finalize...`);

          try {
            // Finalize the partial upload (handles IndexedDB sync internally, with GCS-only fallback)
            await resumableUploadManager.finalizePartialUpload(item.id, item.system_metadata?.gcs_upload_url);
            console.log(`Successfully finalized partial GCS upload for audio ID ${item.id}`);
            message.success("Recording processed successfully.");
            fetchNotes(currentPage);
            return;
          } catch (partialError) {
            console.warn(`Failed to finalize partial GCS upload for audio ID ${item.id}:`, partialError);
            // Clear the failed session and fall back to IndexedDB upload
            resumableUploadManager.clearSession(item.id);
          }
        }

        // Fallback or no partial upload: Upload complete audio from IndexedDB
        console.log(`Uploading complete audio from IndexedDB for audio ID ${item.id}`);
        await uploadFileFromIndexedDBForTranscription(item.id.toString());
        fetchNotes(currentPage);
        return;
      }

      // WebSocket mode: use existing ProcessAudioView endpoint
      await handleSubmitNoteForProcessing({
        audio_id: item.id,
      });
      fetchNotes(currentPage);
    } catch (error) {
      console.error("Error submitting note for processing:", error);
      Swal.fire({
        icon: "error",
        title: "Note processing failed.",
        text: "The audio might not exist on this device or is too short.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleTimeoutRetry = async (notes: TNotes) => {
    trackEvent(EventType.RETRY_TIMEOUT, {
      recordingId: notes.id,
      modalityType: notes.modality_type,
      noteType: notes.note_type,
      errorStatus: notes.error_status,
    });
    setRetryInProgress(true);
    const element = document.getElementById("notes-container");
    const elementMobile = document.getElementById("notes-container-mobile");
    if (element && !isMobile) {
      element?.scrollTo(0, 0);
    } else if (elementMobile) {
      elementMobile?.scrollTo(0, 0);
    }
    if (showPaymentModal(userInfo)) {
      trackEvent(EventType.FREE_TRIAL_LIMIT_MODAL);
      handleOpenPaymentModal();
      dispatch(setCreateModalState(false));
      setRetryInProgress(false);
      return;
    }
    try {
      await handleWriteNoteSubmission({
        audio_id: notes?.id,
        title: notes.title,
        note_type: notes.note_type,
        category_type: notes.category_type,
        modality_mode: ModalityMode[notes.modality_type],
        gender_type: notes.gender_type,
        client_id: notes.client,
        duration_type: notes.duration_type,
        duration_addon_type: notes.duration_addon_type,
        location_type: notes.location_type,
      });
      fetchNotes(currentPage);
    } catch (error) {
      console.error("Error updating note:", error);
      Swal.fire({
        icon: "error",
        title: "Note submission failed.",
        text: "Please try again.",
      });
    } finally {
      setRetryInProgress(false);
    }
  };

  // This is a new way to retry by uploading the audio from indexedDB to the backend
  // Note: This is different from what `uploadAudioFromIndexedDB` does in the home page - that one uploads the audio file to GCS as a backup to what was sent to the backend through websockets
  const handleRetryAsUpload = async () => {
    trackEvent(EventType.RETRY_UPLOAD_FROM_INDEXEDDB, {
      recordingId: item.id,
      modalityType: item.modality_type,
      noteType: item.note_type,
    });
    const loading = message.loading("Uploading audio in background..", 0);
    const value = item.title || "Untitled Session";

    try {
      const file = await assembleBlob(item.id.toString());
      if (!file || file.size === 0) {
        loading();
        message.error("No backup audio file found");
        return;
      }
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("title", value);
      formData.append("note_type", item.note_type.toString());
      formData.append("category_type", item.category_type.toString());
      formData.append("modality_mode", ModalityMode[item.modality_type]);
      formData.append("language_type", item.language_type.toString());
      formData.append("gender_type", item.gender_type.toString());
      if (recordingDetail.location_type !== undefined && recordingDetail.location_type !== null)
        formData.append("location_type", recordingDetail.location_type.toString());
      if (recordingDetail.duration_type !== undefined && recordingDetail.duration_type !== null)
        formData.append("duration_type", recordingDetail.duration_type.toString());

      if (recordingDetail.duration_addon_type !== undefined && recordingDetail.duration_addon_type !== null)
        formData.append("duration_addon_type", recordingDetail.duration_addon_type.toString());

      formData.append("client_id", item?.client?.toString() || "");
      formData.append("system_metadata", JSON.stringify({ ...item.system_metadata, upload_backup_parent_id: item.id }));

      const response = await handleUploadAudioSubmission(formData);
      loading();

      console.log("File Upload Response:", response);
      if (response.status === 201) {
        message.success("Audio re-uploaded successfully. Processing in background..");
        eventEmitter.emit("refreshNotes");
        eventEmitter.emit("noteCreated", response.data);
      } else {
        throw new Error("Error uploading backup");
      }
    } catch (error) {
      loading();
      console.error("Retry Upload Error:", error);
      Swal.fire({
        icon: "error",
        title: "Error uploading backup.",
        text: "Please try again from the device you recorded this on.",
      });
    }
  };

  const onMoreMenuClick: MenuProps["onClick"] = async ({ key, domEvent }) => {
    domEvent?.stopPropagation();
    if (key === "retry") {
      await handleRetryAsUpload();
    }
  };

  const htmlString = React.useMemo(() => {
    return getNotesHTMLStringFromItem(item);
  }, [item]);

  const isCurrentSession = React.useMemo(
    () => (item: TNotes) => {
      return (
        item.status === 2 &&
        item.id === recordingDetail.current_recording_id &&
        (item.modality_type === 0 || item.modality_type === 1)
      );
    },
    [recordingDetail.current_recording_id],
  );

  const handleUnlock = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    if (isMobilePath && window && window.ReactNativeWebView && window.ReactNativeWebView.postMessage) {
      //@ts-ignore
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "showFreePaymentModal",
          recordingId: item.id,
          client_id: item.client,
        }),
      );
      return;
    }
    handleOpenPaymentModal();
  };

  const SharedNotePill = () => {
    return (
      <span
        style={{
          color: "#EA580C",
          background: "#FFF7ED",
          padding: "2px 20px",
          borderRadius: 10,
        }}
      >
        <svg
          style={{ marginRight: 4 }}
          width="13"
          height="10"
          viewBox="0 0 13 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6.49068 2.1875C6.49068 2.76766 6.26021 3.32406 5.84998 3.7343C5.43974 4.14453 4.88334 4.375 4.30318 4.375C3.72302 4.375 3.16662 4.14453 2.75638 3.7343C2.34615 3.32406 2.11568 2.76766 2.11568 2.1875C2.11568 1.60734 2.34615 1.05094 2.75638 0.640704C3.16662 0.230468 3.72302 0 4.30318 0C4.88334 0 5.43974 0.230468 5.84998 0.640704C6.26021 1.05094 6.49068 1.60734 6.49068 2.1875ZM8.59068 8.75525C8.68693 9.2295 8.28618 9.625 7.80318 9.625H0.803179C0.319304 9.625 -0.0805708 9.2295 0.0139292 8.75525C0.215223 7.76628 0.752113 6.87727 1.53369 6.23875C2.31527 5.60023 3.2935 5.25144 4.30274 5.25144C5.31199 5.25144 6.29022 5.60023 7.07179 6.23875C7.85337 6.87727 8.39026 7.76628 8.59155 8.75525H8.59068ZM11.3049 8.75H9.91368C9.90687 8.66482 9.89518 8.5801 9.87868 8.49625C9.71183 7.67058 9.36299 6.8925 8.85755 6.21863C9.50069 6.04161 10.1871 6.11591 10.7775 6.42645C11.3678 6.73699 11.8179 7.2605 12.0364 7.89075C12.1939 8.3475 11.7888 8.75 11.3049 8.75ZM9.55318 5.25C10.0173 5.25 10.4624 5.06563 10.7906 4.73744C11.1188 4.40925 11.3032 3.96413 11.3032 3.5C11.3032 3.03587 11.1188 2.59075 10.7906 2.26256C10.4624 1.93437 10.0173 1.75 9.55318 1.75C9.08905 1.75 8.64393 1.93437 8.31574 2.26256C7.98755 2.59075 7.80318 3.03587 7.80318 3.5C7.80318 3.96413 7.98755 4.40925 8.31574 4.73744C8.64393 5.06563 9.08905 5.25 9.55318 5.25Z"
            fill="#F97316"
          />
        </svg>
        Shared
      </span>
    );
  };

  const canSubmitDraftNoteForProcessing =
    (item.status === 2 && !isCurrentSession(item) && !isLockedNoteOfANonPayingUser(item, userInfo)) ||
    (item.status === 2 && item.system_metadata?.is_from_mobile && item.is_incapture) ||
    (item.status === 2 && item.system_metadata?.from_copilot && item.is_incapture);

  const shouldHideProcessNowButton =
    (item?.system_metadata?.is_from_mobile && !isMobilePath) ||
    item?.system_metadata?.from_copilot ||
    (shouldEnableGCSRecording(userData?.id) &&
      !resumableUploadManager.hasPartialUploadAvailable(item.id) &&
      !item.system_metadata?.gcs_upload_url &&
      !isMobilePath);

  const uploadFailedNote = item.status === 2 && item.modality_type === ModalityMode.upload;

  // Check if this note can be resumed
  const canResume = React.useMemo(() => {
    return !shouldHideProcessNowButton; // Same as submit/process now actually
  }, [shouldHideProcessNowButton]);

  const canResumeForMobile = React.useMemo(() => {
    if (!isMobilePath || !item.system_metadata?.is_from_mobile || !item.uploaded_at) {
      return false;
    }

    if (item.status !== 2) {
      return false;
    }

    if (item.modality_type !== ModalityMode.dictate && item.modality_type !== ModalityMode.audio) {
      return false;
    }
    const uploadedAt = new Date(item.uploaded_at);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return uploadedAt > sevenDaysAgo;
  }, [isMobilePath, item.system_metadata?.is_from_mobile, item.uploaded_at, item.status, item.modality_type]);

  // Check if there's an active recording in progress
  const hasActiveRecording = React.useMemo(() => {
    return recordingDetail.current_recording_id !== null && (isRecording || isPaused);
  }, [recordingDetail, isRecording, isPaused]);

  const handleResumeRecording = async (note: TNotes) => {
    if (
      canResumeForMobile &&
      //@ts-ignore
      window?.ReactNativeWebView?.postMessage
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          type: "resumeRecording",
          audioId: note.id,
        }),
      );
      return;
    }
    setIsResuming(true);
    setResumeAudioId(note.id);

    try {
      // 1. Validate GCS session is still active
      const isValid = await resumableUploadManager.validateSession(note.id);
      if (!isValid) {
        Swal.fire({
          icon: "error",
          title: "Session Expired",
          text: "The recording session has expired (7+ days old). Please use 'Submit' to process from backup.",
        });
        trackEvent(EventType.RESUME_RECORDING_FAILED, {
          audioId: note.id,
          reason: "session_expired",
        });
        return;
      }

      // 2. Restore session from localStorage
      const restored = await resumableUploadManager.restoreSession(note.id);
      if (!restored) {
        throw new Error("Failed to restore recording session");
      }

      // 3. Get session info (recordingTime) for state hydration
      const sessionInfo = resumableUploadManager.getSessionInfoForResume(note.id);
      if (!sessionInfo) {
        throw new Error("Session info not found");
      }

      // 4. Hydrate Redux with note object data
      dispatch(
        setRecordingDetail({
          title: note.title,
          note_type: note.note_type,
          category_type: note.category_type,
          modality_type: String(note.modality_type), // FIXME: actually modality_type should be like "audio", "dictate", "upload", "write", "none"
          gender_type: note.gender_type,
          language_type: note.language_type,
          location_type: note.location_type,
          duration_type: note.duration_type,
          duration_addon_type: note.duration_addon_type,
          client_id: note.client,
          current_recording_id: note.id,
        }),
      );

      // 5. Show recording view
      dispatch(setShowRecordingView(true));
      dispatch(setUploadType(0));

      // 6. Trigger recording start via event emitter
      eventEmitter.emit("resumeRecording", {
        audioId: note.id,
        startTime: sessionInfo.recordingTime,
      });

      trackEvent(EventType.RESUME_RECORDING, {
        audioId: note.id,
        previousDuration: sessionInfo.recordingTime,
        uploadedBytes: sessionInfo.uploadedBytes,
      });
    } catch (error) {
      console.error("Error resuming recording:", error);
      Sentry.captureException(error, { extra: { audioId: note.id } });
      Swal.fire({
        icon: "error",
        title: "Resume Failed",
        text: "Could not resume recording. Please try submitting instead.",
      });
      trackEvent(EventType.RESUME_RECORDING_FAILED, {
        audioId: note.id,
        error: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsResuming(false);
      setResumeAudioId(null);
    }
  };

  return (
    <Flex
      key={item.id}
      ref={index === 0 ? noteRef : null}
      vertical
      className={`${styles[`${componentName}__container-left-row`]} ${
        item.error_status ||
        (item.status !== 1 && isMaxTimedOut(item) && styles[`${componentName}__container-left-row-error`])
      } ${
        isLockedNoteOfANonPayingUser(item, userInfo) && !item.is_incapture
          ? styles[`${componentName}__container-left-row-draft`]
          : ""
      } ${item.id}`}
      onClick={() =>
        isLockedNoteOfANonPayingUser(item, userInfo) ? handleOpenPaymentModal() : navigateToNotesDetail(item)
      }
    >
      {/* Row 1: Date + Title + Actions */}
      <Flex justify="space-between" align="center">
        <Flex align="center" className={styles[`${componentName}__container-left-row-meta-left`]}>
          <CalendarOutlined style={{ color: "#52565F", fontSize: 13 }} />
          <span style={{ color: "#52565F", fontSize: 14, fontWeight: 500, whiteSpace: "nowrap" }}>
            {dayjs(item.uploaded_at).format("MMM D, YYYY, h:mm A")}
          </span>
          <span className={styles[`${componentName}__container-left-row-meta-dot`]}>&middot;</span>
          <span className={styles[`${componentName}__container-left-row-meta-title`]}>
            {item?.title || "Untitled Session"}
          </span>
        </Flex>
        <Flex align="center" gap={12} style={{ flexShrink: 0 }}>
          <Popconfirm
            title="Delete Note"
            description="Are you sure to delete this note?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteRecording(item?.id);
            }}
            okText="Yes"
            onCancel={(e) => e?.stopPropagation()}
            cancelText="No"
            placement={isMobile ? "left" : "top"}
          >
            <DeleteOutlined
              style={{ color: "grey" }}
              className={isMobilePath || isMobile ? "" : styles[`${componentName}__container-left-row-delete`]}
              onClick={(e) => e.stopPropagation()}
            />
          </Popconfirm>
          {canRetry && (
            <Dropdown menu={{ items: moreMenuItems, onClick: onMoreMenuClick }} trigger={["click"]}>
              <MoreOutlined
                style={{ color: "grey", fontSize: 16, padding: "4px 2px", border: "1px solid grey", borderRadius: 8 }}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          )}
        </Flex>
      </Flex>
      {/* Row 2: Patient Name + Gender + Status Badges */}
      <Flex align="center" gap={8} style={{ marginTop: 8 }}>
        <Typography.Text strong style={{ fontSize: 16, color: "#111827", fontWeight: 600, lineHeight: "140%" }}>
          {item?.client_name || "No Client"}
        </Typography.Text>
        {item?.gender_type === GENDER_TYPE.MALE && <span style={{ color: "#6b7280", fontSize: 14 }}>(M)</span>}
        {item?.gender_type === GENDER_TYPE.FEMALE && <span style={{ color: "#6b7280", fontSize: 14 }}>(F)</span>}
        {item.status === 1 && item.error_status === 0 && !item.is_read && <Badge count="New" color="#95de64" />}
        {item.status === 2 && !item?.system_metadata?.is_from_mobile && <Badge count="Draft" color="#faad14" />}
      </Flex>
      {/* Row 3: Pill Badges */}
      <Flex wrap="wrap" gap={6} style={{ marginTop: 8 }}>
        {item?.note_type_info?.name && (
          <span className={styles["pill-note-type"]}>Note Type: {item.note_type_info.name}</span>
        )}
        {item?.client_dob && <span className={styles["pill-info"]}>DOB: {item.client_dob}</span>}
        {item?.client_mrn && <span className={styles["pill-info"]}>MRN: {item.client_mrn}</span>}
      </Flex>
      {item.status !== 1 && item.status !== 2 && item.error_status === 0 && isMaxTimedOut(item) && (
        <Flex align="center" gap={6} className={styles[`${componentName}__container-left-error`]}>
          <ExclamationCircleOutlined />
          <span>Processing exceeded limits, our team is on it.</span>
          {item.status === 4 && (
            <Button
              type="primary"
              disabled={retryInProgress}
              icon={<UndoOutlined />}
              size="small"
              className={styles[`${componentName}__container-left-error-retry`]}
              onClick={() => handleTimeoutRetry(item)}
            >
              Retry
            </Button>
          )}
        </Flex>
      )}
      {item.status !== 1 && item.error_status === 0 && !isMaxTimedOut(item) && (
        <>
          {item.status === 2 && isCurrentSession(item) && item.is_incapture && (
            <Flex align="center" gap={6} className={styles[`${componentName}__container-left-text-processing`]}>
              <LoadingOutlined />
              <span>Draft - In progress</span>
            </Flex>
          )}
          {item.status === 0 && (
            <Flex align="center" gap={6} className={styles[`${componentName}__container-left-text-processing`]}>
              <LoadingOutlined />
              <span>Processing</span>
            </Flex>
          )}
          {item.status === 3 && (
            <Flex align="center" gap={6} className={styles[`${componentName}__container-left-text-processing`]}>
              <LoadingOutlined />
              <span>Transcribing</span>
            </Flex>
          )}
          {item.status === 4 && (
            <Flex align="center" gap={6} className={styles[`${componentName}__container-left-text-processing`]}>
              <LoadingOutlined />
              <span>Generating note</span>
            </Flex>
          )}
          {item.status !== 2 ? (
            <div className={styles[`${componentName}__container-left-text-sub`]}>Processing can take 30-90 seconds</div>
          ) : null}
        </>
      )}
      {canSubmitDraftNoteForProcessing || uploadFailedNote ? (
        <Typography className={styles[`${componentName}__container-left-info`]}>
          <Flex gap={12} align="center" wrap="wrap" style={{ width: "100%" }}>
            <Flex align="center" gap={10} style={{ flex: "1 1 auto", minWidth: "200px" }}>
              <ExclamationCircleOutlined style={{ paddingTop: 1 }} />
              {uploadFailedNote
                ? "Your previous upload failed. Please reupload your audio file."
                : item && item.system_metadata && item.system_metadata.is_from_mobile && !isMobilePath
                  ? `This item is still in draft. Continue on mobile app to process`
                  : item && item.system_metadata && item.system_metadata.from_copilot
                    ? `This item is still in draft. Continue on Nudge Copilot (browser extension) to process`
                    : shouldHideProcessNowButton
                      ? "This session is still in draft and needs to be processed. Please open Nudge on the device you recorded it on to finish processing."
                      : "This session is still in draft and needs to be processed."}
            </Flex>
            {!shouldHideProcessNowButton && (
              <Flex gap={8} align="center" style={{ flex: "0 0 auto" }}>
                {(canResume || canResumeForMobile) && (
                  <Button
                    type="default"
                    icon={<PlayCircleOutlined />}
                    size="middle"
                    className={styles[`${componentName}__container-left-error-retry`]}
                    onClick={() => handleResumeRecording(item)}
                    loading={isResuming && resumeAudioId === item.id}
                    disabled={hasActiveRecording}
                  >
                    Resume
                  </Button>
                )}
                <Button
                  type="primary"
                  icon={<UndoOutlined />}
                  size="middle"
                  className={styles[`${componentName}__container-left-error-retry`]}
                  onClick={() => handleSubmitForProcessing(item)}
                  loading={isUploading}
                  disabled={isResuming}
                >
                  Submit
                </Button>
              </Flex>
            )}
          </Flex>
        </Typography>
      ) : isLockedNoteOfANonPayingUser(item, userInfo) && !item.is_incapture ? (
        <Flex align="center" gap={10}>
          <Typography.Paragraph
            className={styles[`${componentName}__container-left-text`]}
            style={{ filter: "blur(3px)" }}
          >
            <Tooltip title="Click here to upgrade and view the note">
              <span>This note is locked. Please subscribe to see its contents.</span>
            </Tooltip>
          </Typography.Paragraph>
          <Button
            type="primary"
            icon={<UnlockOutlined />}
            size="small"
            className={styles[`${componentName}__container-left-error-retry`]}
            onClick={(event) => handleUnlock(event)}
          >
            Unlock Note
          </Button>
        </Flex>
      ) : null}
      {item.error_status > 0 ? (
        <>
          {item.error_status === 4 && (
            <Typography className={styles[`${componentName}__container-left-error`]}>
              <ExclamationCircleOutlined /> &nbsp; Not enough information in the session. Please ensure your mic and
              sound are working. &nbsp;{" "}
              <Button
                type="primary"
                icon={<UndoOutlined />}
                size="small"
                className={styles[`${componentName}__container-left-error-retry`]}
                onClick={() => handleTimeoutRetry(item)}
              >
                Retry
              </Button>
            </Typography>
          )}
          {item.error_status !== 4 && item.status !== 4 && isMaxTimedOut(item) && item.error_status !== 1 ? (
            <Typography className={styles[`${componentName}__container-left-error`]}>
              <ExclamationCircleOutlined /> &nbsp; Processing exceeded limits, our team is on it.&nbsp;{" "}
              <Button
                type="primary"
                icon={<UndoOutlined />}
                onDoubleClick={(e) => e.stopPropagation()}
                disabled={retryInProgress}
                size="small"
                className={styles[`${componentName}__container-left-error-retry`]}
                onClick={() => handleTimeoutRetry(item)}
              >
                Retry
              </Button>
            </Typography>
          ) : item.error_status !== 4 ? (
            <Typography className={styles[`${componentName}__container-left-error`]}>
              {item.error_status === 1 && (item.modality_type === 0 || item.modality_type === 1) ? (
                <div>
                  <ExclamationCircleOutlined /> &nbsp; There was an error processing this recording. Please click the
                  three dots button and select "Retry upload" before the backup expires in{" "}
                  {PURGE_EXPIRED_AUDIO_FILES_DAYS} days.
                </div>
              ) : (
                <div>
                  <ExclamationCircleOutlined /> &nbsp; Error processing this session recording. &nbsp;{" "}
                  <Button
                    type="primary"
                    icon={<UndoOutlined />}
                    size="small"
                    className={styles[`${componentName}__container-left-error-retry`]}
                    onClick={() => handleTimeoutRetry(item)}
                  >
                    Retry
                  </Button>
                </div>
              )}
            </Typography>
          ) : (
            ""
          )}
        </>
      ) : (
        item.status === 1 && (
          <Typography.Paragraph className={styles[`${componentName}__container-left-text`]}>
            <span
              dangerouslySetInnerHTML={{
                __html: htmlString,
              }}
            />
          </Typography.Paragraph>
        )
      )}
      {/* Shared note pill */}
      {!item.is_owned && item.is_owned !== undefined && item.is_owned !== null && (
        <Flex style={{ marginTop: 8 }}>
          <SharedNotePill />
        </Flex>
      )}
    </Flex>
  );
};

export default NotesList;
